import { useCallback, useMemo, useState } from 'react'

import BlockIcon from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import { Box, Button, Chip, Grid, Tooltip } from '@mui/material'
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import { toast } from 'react-toastify'

import { ConfirmDialog, FplDataGrid, IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT } from 'constants/index'
import {
  WebhookSubscriptionDetailsFragment,
  useSetWebhookSubscriptionEnabledMutation,
} from 'generated/graphql'
import { GET_PAGED_WEBHOOK_SUBSCRIPTIONS } from 'graphql/queries'
import { Restricted, usePermission } from 'providers'

import WebhookDeliveriesDialog from './WebhookDeliveriesDialog'
import WebhookSubscriptionDialog from './WebhookSubscriptionDialog'

const formatDate = (value?: string | null) => value && format(parseJSON(value), DATE_TIME_FORMAT)

interface IProps {
  customerId: number
}

const CustomerWebhooks = (props: IProps) => {
  const { customerId } = props

  const [isAllowedToManage] = usePermission('ManageCustomerWebhooks')

  const [editDialog, setEditDialog] = useState<{
    open: boolean
    subscription: WebhookSubscriptionDetailsFragment | null
  }>({ open: false, subscription: null })
  const [historySubscriptionId, setHistorySubscriptionId] = useState<number | null>(null)
  const [toggleTarget, setToggleTarget] = useState<WebhookSubscriptionDetailsFragment | null>(null)

  const [setEnabled, { loading: toggling }] = useSetWebhookSubscriptionEnabledMutation({
    refetchQueries: ['GetPagedWebhookSubscriptions'],
    onCompleted: (data) => {
      toast.success(
        `Webhook subscription was ${
          data.setWebhookSubscriptionEnabled.isEnabled ? 'enabled' : 'disabled'
        }.`,
      )
    },
  })

  const handleConfirmToggle = () => {
    if (!toggleTarget) return

    setEnabled({
      variables: {
        webhookSubscriptionId: Number(toggleTarget.id),
        isEnabled: !toggleTarget.isEnabled,
      },
    })
  }

  const getActions = useCallback(
    ({ row }: GridRowParams<WebhookSubscriptionDetailsFragment>) => {
      const actions = [
        <GridActionsCellItem
          key='history'
          label='Delivery history'
          icon={
            <Tooltip title='Delivery history' arrow>
              <HistoryIcon />
            </Tooltip>
          }
          onClick={() => setHistorySubscriptionId(Number(row.id))}
        />,
      ]

      if (isAllowedToManage) {
        actions.push(
          <GridActionsCellItem
            key='edit'
            label='Edit'
            icon={
              <Tooltip title='Edit' arrow>
                <EditIcon />
              </Tooltip>
            }
            onClick={() => setEditDialog({ open: true, subscription: row })}
          />,
          <GridActionsCellItem
            key='toggle'
            label={row.isEnabled ? 'Disable' : 'Enable'}
            disabled={toggling}
            icon={
              <Tooltip title={row.isEnabled ? 'Disable' : 'Enable'} arrow>
                {row.isEnabled ? <BlockIcon /> : <CheckCircleOutlineIcon />}
              </Tooltip>
            }
            onClick={() => setToggleTarget(row)}
          />,
        )
      }

      return actions
    },
    [isAllowedToManage, toggling],
  )

  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'isEnabled',
        headerName: 'State',
        minWidth: 110,
        renderCell: ({ value }) => (
          <Chip
            size='small'
            label={value ? 'Enabled' : 'Disabled'}
            color={value ? 'success' : 'default'}
          />
        ),
      },
      { field: 'callbackUrl', headerName: 'Callback URL', minWidth: 280, flex: 1 },
      {
        field: 'eventTypes',
        headerName: 'Events',
        minWidth: 250,
        flex: 1,
        sortable: false,
        valueGetter: ({ value }) => (value as Array<string>)?.join(', '),
      },
      {
        field: 'lastDeliveryStatus',
        headerName: 'Last Delivery',
        minWidth: 200,
        renderCell: ({ row }) => {
          if (!row.lastDeliveryStatus) return '-'

          const label = [row.lastDeliveryStatus, row.lastDeliveryStatusCode]
            .filter(Boolean)
            .join(' / ')

          return (
            <Tooltip title={row.lastDeliveryError ?? ''} arrow>
              <Chip
                size='small'
                label={label}
                color={
                  row.lastDeliveryStatus === 'Delivered'
                    ? 'success'
                    : row.lastDeliveryStatus === 'Failed'
                    ? 'error'
                    : 'default'
                }
              />
            </Tooltip>
          )
        },
      },
      {
        field: 'lastDeliveryAttemptAt',
        headerName: 'Last Attempt',
        minWidth: 160,
        valueFormatter: ({ value }) => formatDate(value as string),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 200,
        renderCell: ({ row }) => `${formatDate(row.createdAt)} ${row.createdBy ?? ''}`,
      },
      {
        field: 'lastModifiedAt',
        headerName: 'Updated',
        minWidth: 200,
        renderCell: ({ row }) =>
          row.lastModifiedAt
            ? `${formatDate(row.lastModifiedAt)} ${row.lastModifiedBy ?? ''}`
            : '-',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 130,
        getActions,
      },
    ],
    [getActions],
  )

  return (
    <Box px={2}>
      <Restricted to='ViewCustomerWebhooks'>
        <FplDataGrid
          query={GET_PAGED_WEBHOOK_SUBSCRIPTIONS}
          entityName='pagedWebhookSubscriptions'
          columns={columns}
          queryVariables={{ customerId }}
          defaultOrder={{ field: 'createdAt', sort: 'desc' }}
          toolbar={{
            caption: 'Webhook Subscriptions',
            leftSide: (
              <Restricted to='ManageCustomerWebhooks'>
                <Grid item>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => setEditDialog({ open: true, subscription: null })}>
                    New Webhook
                  </Button>
                </Grid>
              </Restricted>
            ),
          }}
        />
      </Restricted>

      {editDialog.open && (
        <WebhookSubscriptionDialog
          customerId={customerId}
          subscription={editDialog.subscription}
          openDialog={editDialog.open}
          onCloseDialog={() => setEditDialog({ open: false, subscription: null })}
        />
      )}

      {historySubscriptionId !== null && (
        <WebhookDeliveriesDialog
          webhookSubscriptionId={historySubscriptionId}
          openDialog
          onCloseDialog={() => setHistorySubscriptionId(null)}
        />
      )}

      <ConfirmDialog
        title={toggleTarget?.isEnabled ? 'Disable webhook' : 'Enable webhook'}
        message={
          toggleTarget?.isEnabled
            ? 'The endpoint will stop receiving new events and queued deliveries will be cancelled. Delivery history is kept.'
            : 'The endpoint will start receiving new events again.'
        }
        open={!!toggleTarget}
        setOpen={(open: boolean) => !open && setToggleTarget(null)}
        onConfirm={handleConfirmToggle}
      />
    </Box>
  )
}

export default CustomerWebhooks
